import { fetchJSON as fetchAPI } from './client';

export type ForecastHorizon = '7_day' | '14_day' | '30_day' | '60_day' | '90_day' | '180_day' | '365_day' | 'rolling' | 'custom';
export type VersionStatus = 'draft' | 'working' | 'reviewed' | 'approved' | 'published' | 'archived' | 'historical';
export type AlgorithmType = string;
export type ScenarioType = 'baseline' | 'promotion' | 'price_change' | 'expansion' | 'contraction' | 'market_growth' | 'economic_impact' | 'custom';

export type ForecastRecord = {
  id: string;
  tenant_id: string;
  name: string;
  horizon: ForecastHorizon;
  granularity: string;
  node_label?: string;
  status: VersionStatus;
  selected_algorithm?: AlgorithmType;
  forecast_units: number;
  forecast_revenue: number;
  confidence: number;
  created_at: string;
  updated_at: string;
};

export type ForecastModel = {
  id: string;
  forecast_id: string;
  algorithm: AlgorithmType;
  forecast_values: number[];
  accuracy_mape: number;
  accuracy_mae: number;
  accuracy_rmse: number;
  bias: number;
  stability: number;
  selected: boolean;
  explainability?: string;
  generated_at: string;
};

export type ForecastSignal = {
  id: string;
  forecast_id: string;
  trend: number;
  seasonality: number;
  growth: number;
  volatility: number;
  outlier_count: number;
  lifecycle_stage: string;
  signal_quality: number;
  signal_confidence: number;
  promotion_impact?: number;
};

export type ForecastAccuracy = {
  id: string;
  forecast_id: string;
  model_id: string;
  mape: number;
  wmape: number;
  mae: number;
  rmse: number;
  bias: number;
  fva: number;
  tracking_signal: number;
  stability: number;
  reliability: number;
  historical_accuracy: number;
};

export type ForecastScenario = {
  id: string;
  forecast_id: string;
  name: string;
  type: ScenarioType;
  forecast_units: number;
  growth_pct: number;
  description?: string;
};

export type AlgorithmConfig = {
  id: string;
  algorithm: AlgorithmType;
  enabled: boolean;
  parameters?: Record<string, number>;
  priority: number;
  description?: string;
};

export type ForecastComparison = {
  forecast_id: string;
  models: ForecastModel[];
  selected_model_id: string;
  generated_at: string;
};

export type ForecastDashboard = {
  tenant_id: string;
  active_forecasts: number;
  published_forecasts: number;
  avg_mape: number;
  avg_bias: number;
  algorithms_run: number;
  variance_alerts: number;
  risk_alerts: number;
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type GenerateForecastRequest = {
  name: string;
  horizon: ForecastHorizon;
  granularity: string;
  node_label?: string;
  periods_ahead: number;
  season_length?: number;
  series: { periods: string[]; values: number[] };
};

const API_BASE = import.meta.env.VITE_FORECASTENGINE_API ?? '/api/v1';

function forecastFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const forecastEngineApi = {
  dashboard: () => forecastFetch<ForecastDashboard>('/forecast-dashboard'),
  listForecasts: () => forecastFetch<ForecastRecord[]>('/forecasts'),
  getForecast: (id: string) => forecastFetch<ForecastRecord>(`/forecasts/${id}`),
  generateForecast: (req: GenerateForecastRequest) =>
    forecastFetch<ForecastRecord>('/forecasts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),
  approveForecast: (id: string) =>
    forecastFetch<ForecastRecord>(`/forecasts/${id}/approve`, { method: 'POST' }),
  publishForecast: (id: string) =>
    forecastFetch<ForecastRecord>(`/forecasts/${id}/publish`, { method: 'POST' }),
  listModels: (forecastId?: string) =>
    forecastFetch<ForecastModel[]>(`/forecast-models${forecastId ? `?forecast_id=${forecastId}` : ''}`),
  getComparison: (forecastId: string) =>
    forecastFetch<ForecastComparison>(`/forecast-comparison?forecast_id=${forecastId}`),
  listAccuracies: (forecastId?: string) =>
    forecastFetch<ForecastAccuracy[]>(`/forecast-accuracy${forecastId ? `?forecast_id=${forecastId}` : ''}`),
  getSignals: (forecastId: string) =>
    forecastFetch<ForecastSignal>(`/forecast-signals?forecast_id=${forecastId}`),
  listScenarios: (forecastId?: string) =>
    forecastFetch<ForecastScenario[]>(`/forecast-scenarios${forecastId ? `?forecast_id=${forecastId}` : ''}`),
  listAlgorithms: () => forecastFetch<AlgorithmConfig[]>('/forecast-algorithms'),
  listActivity: (limit = 20) => forecastFetch<ActivityEntry[]>(`/forecast-activity?limit=${limit}`),
};
